import * as composeActions from '@bca-compose/store/compose.actions';
import { ComposeActionTypes } from '@bca-compose/store/compose.actions';
import { cloneProducts, cloneProduct } from '@bca-shared/shared.constants';
import { IProduct } from '@bca-shared/models/product';
import { IRoute } from '@bca-compose/models/route';
import { ProductIdentifier, TravelerClass } from '@bca-shared/models/_enums';
import { ProductId } from '@bca-shared/models/_types';

export const INITIAL_STATE: IState = {
  products: [],
  productsLoading: false,
  stationsLoading: false,
  routeLoading: false,
  selectedProduct: null,
  configuredProduct: null,
  configuredRoute: null,
  totalPrice: 0,
  trainFreePrice: 0,
  serviceTileToggleState: Object(),
};

export interface IState {
  readonly products: IProduct[];
  readonly productsLoading: boolean;
  readonly stationsLoading: boolean;
  readonly routeLoading: boolean;
  readonly selectedProduct: IProduct;
  readonly configuredProduct: IProduct;
  readonly configuredRoute: IRoute;
  readonly totalPrice: number;
  readonly trainFreePrice: number;
  readonly serviceTileToggleState: { [productId: string]: boolean };
}

export function reducer(state: IState = INITIAL_STATE, action: composeActions.Action): IState {
  switch (action.type) {
    case ComposeActionTypes.SetServiceTileToggleState: {
      const { productId, expanded } = action.payload;

      const serviceTileToggleState = { ...state.serviceTileToggleState };
      serviceTileToggleState[productId] = expanded;

      return {
        ...state,
        serviceTileToggleState
      };
    }
    case ComposeActionTypes.SetProducts: {
      const products = action.payload;
      return {
        ...state,
        products,
        trainFreePrice: products.find(product => product.identifier === ProductIdentifier.TreinVrij)
          .price
      };
    }
    case ComposeActionTypes.ProductsLoading: {
      const { loading: productsLoading } = action.payload;
      return {
        ...state,
        productsLoading
      };
    }
    case ComposeActionTypes.RouteLoading: {
      const { loading: routeLoading } = action.payload;
      return {
        ...state,
        routeLoading
      };
    }
    case ComposeActionTypes.StationsLoading: {
      const { loading: stationsLoading } = action.payload;
      return {
        ...state,
        stationsLoading
      };
    }
    case ComposeActionTypes.SelectProduct: {
      const payload = action.payload;

      if (!payload || !payload.product) {
        return {
          ...state,
          selectedProduct: null,
          totalPrice: 0
        };
      }
      const currentSelectedProduct = cloneProduct(state.selectedProduct);
      const { product } = payload;

      const selectedProduct = {
        ...product,
        id: product.id ? product.id : Date.now()
      };

      const totalPrice =
        selectedProduct.price +
        selectedProduct.addons
          .filter(addon => addon.selected)
          .reduce((total, addon) => total + addon.price, 0);

      const mergedImage = !selectedProduct.needsPhoto
        ? null
        : {
            ...(currentSelectedProduct && currentSelectedProduct.user
              ? currentSelectedProduct.user.image
              : null),
            ...(selectedProduct.user ? selectedProduct.user.image : null)
          };

      const image = mergedImage && Object.keys(mergedImage).length ? mergedImage : null;

      const mergedUser = {
        ...(currentSelectedProduct && currentSelectedProduct.user
          ? currentSelectedProduct.user
          : null),
        ...(selectedProduct.user ? selectedProduct.user : null)
      };

      const withImage =
        selectedProduct.needsPhoto &&
        currentSelectedProduct &&
        currentSelectedProduct.user &&
        currentSelectedProduct.user.valid &&
        image != null
          ? true
          : selectedProduct.user && selectedProduct.user.valid
          ? true
          : false;

      const withoutImage =
        !selectedProduct.needsPhoto &&
        currentSelectedProduct &&
        currentSelectedProduct.user &&
        currentSelectedProduct.user.valid
          ? true
          : selectedProduct.user && selectedProduct.user.valid
          ? true
          : false;

      const user =
        mergedUser && Object.keys(mergedUser).length
          ? {
              ...mergedUser,
              image,
              valid: withImage || withoutImage
            }
          : null;

      const mergedProduct = {
        ...currentSelectedProduct,
        ...selectedProduct,
        totalPrice,
        route:
          selectedProduct.identifier === ProductIdentifier.Traject ? selectedProduct.route : null,
        user
      };

      return {
        ...state,
        selectedProduct: mergedProduct,
        totalPrice
      };
    }
    case ComposeActionTypes.SelectAddon: {
      const { addon, selected } = action.payload;

      const selectedProduct = cloneProduct(state.selectedProduct);

      const { identifier } = addon;

      selectedProduct.addons.find(addon => addon.identifier === identifier).selected = selected;

      const addonPrice = selectedProduct.addons.find(addon => addon.identifier === identifier)
        .price;

      return {
        ...state,
        selectedProduct,
        totalPrice: selected ? state.totalPrice + addonPrice : state.totalPrice - addonPrice
      };
    }
    case ComposeActionTypes.SelectService: {
      const { service, selected } = action.payload;

      const selectedProduct = cloneProduct(state.selectedProduct);

      const { identifier } = service;

      selectedProduct.services.find(
        service => service.identifier === identifier
      ).selected = selected;

      return {
        ...state,
        selectedProduct
      };
    }
    case ComposeActionTypes.SetConfiguredProduct: {
      const configuredProduct = action.payload;
      return {
        ...state,
        configuredProduct
      };
    }
    case ComposeActionTypes.SetConfiguredRoute: {
      const configuredRoute = action.payload;
      return {
        ...state,
        configuredRoute
      };
    }
    case ComposeActionTypes.ConfigureProduct: {
      const route = action.payload;
      const selectedProduct = cloneProduct(state.selectedProduct);
      const products = cloneProducts(state.products);
      const { price } = route;

      const configuredProduct = {
        ...products.find(product => product.identifier === ProductIdentifier.Traject),
        price: price[TravelerClass.SECOND],
        route
      };

      configuredProduct.addons.find(addon => addon.clazz === TravelerClass.FIRST).price =
        price[TravelerClass.FIRST];

      return {
        ...state,
        configuredProduct,
        selectedProduct:
          selectedProduct && selectedProduct.identifier === ProductIdentifier.Traject
            ? { ...selectedProduct, ...configuredProduct }
            : selectedProduct
      };
    }
  }

  return state;
}
